/**
 * HelpDrawer — floating help launcher + slide-out drawer with quick tips,
 * tutorial links, contact, keyboard shortcuts.
 *
 * Mounted globally inside AdminShell so it's available on every admin page.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { HelpCircle, X, Mail, BookOpen, PlayCircle, Keyboard, MessageCircleQuestion, Sparkles } from "lucide-react";

const TUTORIALS = [
  { title: "Voice dictation: 5x faster shift notes", duration: "1:12", topic: "voice" },
  { title: "Adding your first client", duration: "0:48", topic: "clients" },
  { title: "Inviting caregivers", duration: "0:55", topic: "team" },
  { title: "Sharing updates with families", duration: "1:05", topic: "family" },
  { title: "Generating invoices in 2 clicks", duration: "1:30", topic: "billing" },
];

const SHORTCUTS = [
  { keys: "⌘ K", label: "Open voice command" },
  { keys: "G then C", label: "Go to Clients" },
  { keys: "G then S", label: "Go to Schedule" },
  { keys: "G then R", label: "Go to Reports" },
  { keys: "/", label: "Focus search (where available)" },
  { keys: "Esc", label: "Close modal / cancel" },
];

export default function HelpDrawer() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[60] flex h-12 w-12 items-center justify-center rounded-full bg-clinical-primary text-white shadow-lg hover:bg-clinical-primary/90 focus:outline-none focus:ring-2 focus:ring-clinical-primary/40"
        aria-label="Open help"
        data-testid="help-fab"
      >
        <HelpCircle className="h-5 w-5" />
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[100] flex justify-end bg-black/40 backdrop-blur-sm"
             onClick={() => setOpen(false)}
             data-testid="help-drawer-backdrop">
          <aside
            className="h-full w-full max-w-md bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="help-drawer"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-clinical-border bg-white px-5 py-4">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-clinical-primary text-white">
                  <Sparkles className="h-4 w-4" />
                </span>
                <div>
                  <div className="font-heading font-semibold text-clinical-text">Help & tips</div>
                  <div className="text-[11px] text-clinical-subtext">We're here to help you ship.</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-2 text-clinical-subtext hover:bg-clinical-bg"
                aria-label="Close help"
                data-testid="help-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* Tutorials */}
              <section data-testid="help-tutorials">
                <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                  <PlayCircle className="h-3.5 w-3.5" /> 60-second walkthroughs
                </div>
                <ul className="space-y-1.5">
                  {TUTORIALS.map((t, i) => (
                    <li key={i}>
                      <a
                        href="https://www.afyanex.com/help"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between gap-2 rounded-lg border border-clinical-border bg-white px-3 py-2 hover:border-clinical-primary/40 transition"
                        data-testid={`help-tutorial-${t.topic}`}
                      >
                        <div className="text-sm text-clinical-text">{t.title}</div>
                        <div className="text-[10px] text-clinical-subtext font-mono">{t.duration}</div>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Knowledge base */}
              <section>
                <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                  <BookOpen className="h-3.5 w-3.5" /> Knowledge base
                </div>
                <a
                  href="https://www.afyanex.com/help"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-lg border border-clinical-border bg-clinical-bg/40 p-3 hover:border-clinical-primary/40 transition"
                  data-testid="help-knowledge-base"
                >
                  <div className="text-sm font-medium text-clinical-text">help.afyanex.com</div>
                  <div className="text-xs text-clinical-subtext mt-0.5">
                    Setup guides, EVV state requirements, troubleshooting.
                  </div>
                </a>
              </section>

              {/* Keyboard shortcuts */}
              <section data-testid="help-shortcuts">
                <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                  <Keyboard className="h-3.5 w-3.5" /> Keyboard shortcuts
                </div>
                <ul className="space-y-1">
                  {SHORTCUTS.map((s, i) => (
                    <li key={i} className="flex items-center justify-between text-sm text-clinical-text">
                      <span>{s.label}</span>
                      <kbd className="rounded border border-clinical-border bg-clinical-bg/60 px-1.5 py-0.5 text-[10px] font-mono">
                        {s.keys}
                      </kbd>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Contact */}
              <section>
                <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                  <MessageCircleQuestion className="h-3.5 w-3.5" /> Talk to us
                </div>
                <a
                  href="mailto:hello@afyanex.com"
                  className="flex items-center gap-2 rounded-lg border border-clinical-border bg-white px-3 py-2.5 text-sm hover:border-clinical-primary/40 transition"
                  data-testid="help-email-support"
                >
                  <Mail className="h-4 w-4 text-clinical-primary" />
                  <div>
                    <div className="text-clinical-text font-medium">hello@afyanex.com</div>
                    <div className="text-[11px] text-clinical-subtext">Usually replies within 4 hours.</div>
                  </div>
                </a>
              </section>
            </div>
          </aside>
        </div>,
        document.body,
      )}
    </>
  );
}
